/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import { Fade, useMediaQuery } from '@material-ui/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Link } from 'components';
import { ContentType, ICustomTheme } from 'interfaces';
import { ROUTES } from '../../constants';
import * as S from './styles';
import { useImgSrc } from 'core/utils/use-img-src.hook';

const { getSkeletonCss } = S;

const ContentCategoryCard = ({
  content,
  skeleton,
  withFade,
}: {
  content?: ContentType;
  skeleton?: { height: number };
  withFade?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const { src: srcImage } = useImgSrc(content?.image);
  const isMobile = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );

  if (skeleton) {
    return (
      <S.StyledCard
        elevation={4}
        square
        css={getSkeletonCss(skeleton.height)}
        isMobile={isMobile}
      />
    );
  }

  if (content) {
    return (
      <Fade in timeout={withFade ? 500 : 0}>
        <S.StyledCard elevation={4} square isMobile={isMobile}>
          {content.image && <S.StyledImage src={srcImage} loading="lazy" />}
          <React.Fragment>
            <S.StyledTitleAndDateContainer isMobile={isMobile}>
              <S.Title>{content.title}</S.Title>
              <S.Date>
                {content.startDate &&
                  moment(content.startDate).format('DD MMMM YYYY')}
              </S.Date>
            </S.StyledTitleAndDateContainer>

            <S.Subtitle paragraph>{content.subtitle}</S.Subtitle>

            <S.Description paragraph>{content.caption}</S.Description>

            <S.StyledLinkContainer>
              <Link
                to={
                  content.isHomepage
                    ? ROUTES.HOME
                    : `${ROUTES.CONTENT}/${content.id}`
                }
                color="primary"
                decoration="none"
                weight="bold"
                position="right"
                isParagraph={false}
              >
                <S.LinkTextWrapper>
                  {t('category.readArticle')}
                  <S.ArrowIcon />
                </S.LinkTextWrapper>
              </Link>
            </S.StyledLinkContainer>
          </React.Fragment>
        </S.StyledCard>
      </Fade>
    );
  }

  return <React.Fragment />;
};

ContentCategoryCard.defaultProps = {
  withFade: true,
};

export default ContentCategoryCard;
